import { useState, useRef, useMemo, useCallback } from 'react';
import { Input, Button, Typography, Tag, notification, InputRef } from 'antd';
import { Plus, X, Check } from 'lucide-react';
import { getTemplateVarsForTrigger } from '../../../utils/preview-constants';
import { safeClipboardWrite } from '../../../utils/preview-utils';

const { Text } = Typography;

interface EnvVar {
    key: string;
    value: string;
}

interface AppEnvVarEditorProps {
    envVars: EnvVar[];
    onChange: (envVars: EnvVar[]) => void;
    triggerEvents?: string[];
}

export default function AppEnvVarEditor({ envVars, onChange, triggerEvents }: AppEnvVarEditorProps) {
    const [newKey, setNewKey] = useState('');
    const [newValue, setNewValue] = useState('');
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const [copiedVar, setCopiedVar] = useState<string | null>(null);
    const newValueRef = useRef<InputRef>(null);
    const editValueRef = useRef<InputRef>(null);

    const handleAdd = () => {
        const trimmedKey = newKey.trim();
        const trimmedValue = newValue.trim();

        if (!trimmedKey || !trimmedValue) return;

        if (envVars.some(v => v.key === trimmedKey)) {
            notification.error({
                message: `Key "${trimmedKey}" already exists`,
                description: 'Edit the existing variable or remove it first.',
                duration: 3,
            });
            return;
        }

        onChange([...envVars, { key: trimmedKey, value: trimmedValue }]);
        setNewKey('');
        setNewValue('');
    };

    const handleRemove = (key: string) => {
        onChange(envVars.filter(v => v.key !== key));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
        }
    };

    const startEdit = (v: EnvVar) => {
        setEditingKey(v.key);
        setEditValue(v.value);
        setTimeout(() => editValueRef.current?.focus(), 0);
    };

    const commitEdit = () => {
        if (editingKey && editValue.trim()) {
            onChange(envVars.map(v =>
                v.key === editingKey ? { ...v, value: editValue.trim() } : v
            ));
        }
        setEditingKey(null);
        setEditValue('');
    };

    const handleEditKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            commitEdit();
        } else if (e.key === 'Escape') {
            setEditingKey(null);
            setEditValue('');
        }
    };

    const insertOrCopyTemplateVar = useCallback((varName: string) => {
        const activeRef = editingKey ? editValueRef : newValueRef;
        const inputEl = activeRef.current?.input as HTMLInputElement | undefined;

        if (inputEl && document.activeElement === inputEl) {
            const start = inputEl.selectionStart ?? inputEl.value.length;
            const end = inputEl.selectionEnd ?? start;
            const currentVal = inputEl.value;
            const newVal = currentVal.slice(0, start) + varName + currentVal.slice(end);

            if (editingKey) {
                setEditValue(newVal);
            } else {
                setNewValue(newVal);
            }

            setTimeout(() => {
                const pos = start + varName.length;
                inputEl.setSelectionRange(pos, pos);
                inputEl.focus();
            }, 0);
            return;
        }

        safeClipboardWrite(varName);
        setCopiedVar(varName);
        setTimeout(() => setCopiedVar(null), 1500);
    }, [editingKey]);

    const filteredVars = useMemo(() => getTemplateVarsForTrigger(triggerEvents), [triggerEvents]);

    const templateVarTags = useMemo(() => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {filteredVars.map(v => (
                <TemplateVarTag key={v} varName={v} copiedVar={copiedVar} onClick={insertOrCopyTemplateVar} />
            ))}
        </div>
    ), [copiedVar, insertOrCopyTemplateVar, filteredVars]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Template variables */}
            <div>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                    Available variables (click to insert or copy)
                </Text>
                {templateVarTags}
            </div>

            {/* Add row */}
            <div style={{ display: 'flex', gap: 6 }}>
                <Input
                    size="small"
                    placeholder="KEY"
                    value={newKey}
                    onChange={e => setNewKey(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{ flex: 1, fontFamily: 'monospace', fontSize: 12 }}
                />
                <Input
                    ref={newValueRef}
                    size="small"
                    placeholder="Value"
                    value={newValue}
                    onChange={e => setNewValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{ flex: 1.5, fontFamily: 'monospace', fontSize: 12 }}
                />
                <Button
                    size="small"
                    type="text"
                    icon={<Plus size={14} />}
                    onClick={handleAdd}
                    disabled={!newKey.trim() || !newValue.trim()}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                />
            </div>

            {/* Existing variables */}
            {envVars.length === 0 ? (
                <div style={{
                    border: '1px dashed #d1d5db',
                    borderRadius: 6,
                    padding: '12px 14px',
                    textAlign: 'center',
                }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Add environment variable overrides to customize app configuration per preview
                    </Text>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {envVars.map(v => (
                        <div
                            key={v.key}
                            style={{
                                padding: '6px 0',
                                borderBottom: '1px solid #f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            <Text strong style={{
                                fontSize: 12,
                                fontFamily: 'monospace',
                                flexShrink: 0,
                                width: '35%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                {v.key}
                            </Text>
                            {editingKey === v.key ? (
                                <Input
                                    ref={editValueRef}
                                    size="small"
                                    value={editValue}
                                    onChange={e => setEditValue(e.target.value)}
                                    onKeyDown={handleEditKeyDown}
                                    onBlur={commitEdit}
                                    style={{ flex: 1, fontFamily: 'monospace', fontSize: 12 }}
                                    autoFocus
                                />
                            ) : (
                                <Text
                                    type="secondary"
                                    onClick={() => startEdit(v)}
                                    title="Click to edit"
                                    style={{
                                        fontSize: 12,
                                        fontFamily: 'monospace',
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        cursor: 'text',
                                        padding: '2px 4px',
                                        borderRadius: 4,
                                        transition: 'background 0.15s',
                                    }}
                                    onMouseEnter={(e: any) => { e.currentTarget.style.background = '#f3f4f6'; }}
                                    onMouseLeave={(e: any) => { e.currentTarget.style.background = 'transparent'; }}
                                >
                                    {v.value}
                                </Text>
                            )}
                            <Button
                                type="text"
                                size="small"
                                icon={<X size={12} />}
                                onClick={() => handleRemove(v.key)}
                                style={{
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#9ca3af',
                                }}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function TemplateVarTag({ varName, copiedVar, onClick }: {
    varName: string;
    copiedVar: string | null;
    onClick: (v: string) => void;
}) {
    const isCopied = copiedVar === varName;
    return (
        <Tag
            aria-label={`Template variable ${varName}`}
            style={{
                cursor: 'pointer',
                fontSize: 11,
                margin: 0,
                userSelect: 'none',
                transition: 'all 0.15s',
                background: isCopied ? '#f0fdf4' : undefined,
                borderColor: isCopied ? '#86efac' : undefined,
                color: isCopied ? '#16a34a' : undefined,
            }}
            onClick={() => onClick(varName)}
        >
            <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                <span style={{ visibility: isCopied ? 'hidden' : 'visible' }}>{varName}</span>
                {isCopied && (
                    <span style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 3,
                    }}>
                        <Check size={10} /> Copied
                    </span>
                )}
            </span>
        </Tag>
    );
}
